import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft,
    Mail,
    MapPin,
    Phone,
    Briefcase,
    Globe,
    ScrollText,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/i18n/LanguageContext";
import { SITE } from "@/lib/site";

const Impressum = () => {
    const { t } = useLanguage();

    useEffect(() => {
        window.scrollTo({ top: 0 });
        const seo = t.page_meta?.seo?.impressum;
        if (seo) document.title = seo.title;
    }, [t]);

    const I = t.legal.impressum;

    return (
        <div data-testid="impressum-page" className="min-h-screen bg-ella-cream">
            <Header />
            <main className="pt-32 md:pt-40 pb-24">
                <div className="ella-container max-w-3xl">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-ella-muted hover:text-ella-ink transition-colors"
                        data-testid="legal-back"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {t.legal.back}
                    </Link>

                    <span className="ella-overline mt-12 block">ELLA Online Coaching</span>
                    <h1
                        className="ella-headline text-4xl md:text-5xl lg:text-6xl mt-3 text-ella-ink leading-[1.05]"
                        data-testid="impressum-title"
                    >
                        {I.title}
                    </h1>
                    <p className="mt-5 text-ella-ink/65 italic font-light leading-relaxed">
                        {I.intro}
                    </p>

                    <div className="mt-10 h-px w-24 bg-ella-gold" />

                    {/* Block: Anbieterin / Proveedora */}
                    <section className="mt-14 space-y-10">
                        <header className="flex items-center gap-4">
                            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-ella-ink text-ella-gold flex-shrink-0">
                                <Briefcase className="w-5 h-5" strokeWidth={1.4} />
                            </span>
                            <div>
                                <p className="ella-overline">01</p>
                                <h2 className="font-display text-2xl md:text-3xl text-ella-ink leading-tight mt-1">
                                    {I.provider_heading}
                                </h2>
                            </div>
                        </header>

                        <div className="pl-16 space-y-3 text-ella-ink/85 leading-relaxed font-light">
                            <p>
                                <strong className="font-display text-ella-ink not-italic">
                                    {SITE.brand}
                                </strong>
                                <br />
                                {I.legal_form}
                                <br />
                                {SITE.coachName}
                            </p>
                            <p className="inline-flex items-start gap-2">
                                <MapPin
                                    className="w-4 h-4 mt-1 text-ella-gold-dark flex-shrink-0"
                                    strokeWidth={1.5}
                                />
                                <span>
                                    {I.address_pending}
                                    <br />
                                    <em className="text-ella-muted text-sm">
                                        {I.address_note}
                                    </em>
                                </span>
                            </p>
                            <p className="inline-flex items-center gap-2">
                                <Phone
                                    className="w-4 h-4 text-ella-gold-dark"
                                    strokeWidth={1.5}
                                />
                                <a
                                    href={`tel:${SITE.contact.phoneIntl.replace(/\s/g, "")}`}
                                    className="hover:text-ella-gold-dark transition-colors"
                                >
                                    {SITE.contact.phoneIntl}
                                </a>
                            </p>
                            <p className="inline-flex items-center gap-2">
                                <Mail
                                    className="w-4 h-4 text-ella-gold-dark"
                                    strokeWidth={1.5}
                                />
                                <a
                                    href={`mailto:${SITE.contact.email}`}
                                    className="hover:text-ella-gold-dark transition-colors"
                                >
                                    {SITE.contact.email}
                                </a>
                            </p>
                            <p className="inline-flex items-center gap-2">
                                <Globe
                                    className="w-4 h-4 text-ella-gold-dark"
                                    strokeWidth={1.5}
                                />
                                <span>{I.website}</span>
                            </p>
                        </div>
                    </section>

                    {/* Block: Verantwortlich / Responsable */}
                    <section className="mt-16 space-y-6">
                        <header className="flex items-center gap-4">
                            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-ella-ink text-ella-gold flex-shrink-0">
                                <ScrollText className="w-5 h-5" strokeWidth={1.4} />
                            </span>
                            <div>
                                <p className="ella-overline">02</p>
                                <h2 className="font-display text-2xl md:text-3xl text-ella-ink leading-tight mt-1">
                                    {I.responsible_heading}
                                </h2>
                            </div>
                        </header>
                        <p className="pl-16 text-ella-ink/85 leading-relaxed font-light">
                            {I.responsible_body}
                        </p>
                    </section>

                    {/* Disclaimer */}
                    <section className="mt-16 space-y-6">
                        <header className="flex items-center gap-4">
                            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-ella-ink text-ella-gold flex-shrink-0 font-display text-base">
                                03
                            </span>
                            <div>
                                <h2 className="font-display text-2xl md:text-3xl text-ella-ink leading-tight">
                                    {I.disclaimer_heading}
                                </h2>
                            </div>
                        </header>
                        <div className="pl-16 space-y-4 text-ella-ink/80 leading-relaxed font-light text-[15.5px]">
                            <p>{I.disclaimer_content}</p>
                            <p>{I.disclaimer_links}</p>
                        </div>
                    </section>

                    {/* Copyright */}
                    <section className="mt-16 space-y-6">
                        <header className="flex items-center gap-4">
                            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-ella-ink text-ella-gold flex-shrink-0 font-display text-base">
                                04
                            </span>
                            <div>
                                <h2 className="font-display text-2xl md:text-3xl text-ella-ink leading-tight">
                                    {I.copyright_heading}
                                </h2>
                            </div>
                        </header>
                        <p className="pl-16 text-ella-ink/80 leading-relaxed font-light text-[15.5px]">
                            {I.copyright_body}
                        </p>
                    </section>

                    <p className="mt-20 pt-10 border-t border-ella-line italic text-ella-muted text-sm leading-relaxed">
                        © {new Date().getFullYear()} ELLA Online Coaching ·{" "}
                        {SITE.coachName} · {I.jurisdiction}
                    </p>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default Impressum;
